import React, { useState, useEffect } from 'react';
import styles from '../../../../assets/styles/organisationManagement.module.css';
import AuthLayout from '../../../../layout/AuthLayout';
import RightPanelTopMenu from '../../common/RightPanelTopMenu';
import { useNavigate, useParams } from 'react-router-dom';
import { CallWithAuth } from '../../../../action/apiActions';
import { useToast } from '../../common/Toast';
import { GET_ORGANISATION } from '../../../../action/apiPath';
import closeIcon from '../../../../assets/images/closeSvgIcon.svg';

interface OrganisationInfo {
  no_of_users: number | string;
  no_of_questions: number | string;
}

const CreateOrganization: React.FC = () => {
  const [status, setStatus] = useState<'active' | 'inactive'>('active');
  const [showModal, setShowModal] = useState(false);
  const [tempStatus, setTempStatus] = useState<'active' | 'inactive'>('active');

  const navigate = useNavigate();
  const BASE_URL: string = process.env.REACT_APP_API_KEY ?? '';
  const toast = useToast();
  const { orgId } = useParams();
  const [activeStatus, setActiveStatus] = useState(null);

  const [organisationInfo, setOrganisationInfo] = useState({
    name: '',
    is_active: true,
    no_of_users: '',
    no_of_questions: '',
    is_infinite: false,
  });

  const getOrganisationInfo = async () => {
    try {
      const response = await CallWithAuth('GET', `${GET_ORGANISATION}${orgId}/get-org-details/`);
      if (response?.res?.status === 200 && response?.res?.data) {
        const data = response.res.data.data;

        // Ensure numeric fields are stored as numbers
        setOrganisationInfo({
          ...data,
          no_of_users: Number(data.no_of_users),
          no_of_questions: Number(data.no_of_questions),
        });

        setStatus(data.is_active ? 'active' : 'inactive');
        setActiveStatus(data.is_active);
      }
    } catch (error) {
      console.error('Error fetching organization details:', error);
    }
  };

  useEffect(() => {
    getOrganisationInfo();
  }, [orgId]);

  const handleAddOrganization = async () => {
    try {
      let apiUrl = `${BASE_URL}create-organization/`;
      let method: 'POST' | 'PUT' = 'POST';

      const payload = {
        ...organisationInfo,
        no_of_users: Number(organisationInfo.no_of_users),
        no_of_questions: Number(organisationInfo.no_of_questions),
      };

      if (orgId) {
        apiUrl = `${BASE_URL}organizations/${orgId}/update-organization/`;
        method = 'PUT';
        if (payload.is_infinite) {
          payload.no_of_questions = 0;
        }
      }

      const response = await CallWithAuth(method, apiUrl, payload);
      if (response?.res?.status === 200) {
        toast?.add(response.res.data?.message, 'success');
        if (orgId) {
          navigate('/organization-management/details/' + orgId);
        } else {
          navigate('/organization-management');
        }
      } else {
        toast?.remove('all');
        toast?.add(response?.res?.data?.error, 'error');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'An error occurred while processing.';
      toast?.remove('all');
      toast?.add(errorMessage, 'error');
    }
  };

  const handleBackClick = () => {
    navigate('/organization-management');
  };

  const handleStatusChange = (newStatus: 'active' | 'inactive') => {
    if (newStatus === 'inactive') {
      setTempStatus('inactive'); // Temporarily store the new status
      setShowModal(true); // Open confirmation modal
    } else {
      setStatus('active');
      setOrganisationInfo({ ...organisationInfo, is_active: true });
    }
  };

  const confirmStatusChange = () => {
    setStatus(tempStatus);
    setOrganisationInfo({ ...organisationInfo, is_active: tempStatus === 'active' });
    setShowModal(false);
  };

  // Function to close the modal
  const closeModal = () => {
    setShowModal(false);
  };

  return (
    <AuthLayout>
      <RightPanelTopMenu setQuestionBankId={null} title="Organisation Management" />
      <button
        className={styles.org_back_button}
        onClick={handleBackClick}
        onKeyDown={(e) => e.key === 'Enter' && handleBackClick()}
        style={{ background: 'none', border: 'none' }}
      >
        <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M16.4023 19.422L9.88687 12.902C9.11741 12.132 9.11741 10.872 9.88687 10.102L16.4023 3.58203"
            stroke="#212529"
            strokeWidth="1.5"
            strokeMiterlimit="10"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        Back
      </button>

      <div className={styles.formContainer}>
        <h1 className={styles.form_title}>{orgId ? 'Update' : 'Add'} Organization</h1>
        <form className={styles.form}>
          <div className={styles.formGroup}>
            <label htmlFor="organizationName" className={styles.name_label}>
              Organization Name <span className={styles.required_name}>*</span>
            </label>
            <input
              id="organizationName"
              type="text"
              value={organisationInfo?.name}
              onChange={(e) => setOrganisationInfo({ ...organisationInfo, name: e.target.value })}
              className={styles.name_input_first}
            />
          </div>

          <div className={styles.mainForm}>
            <div className={styles.formGroup}>
              <label htmlFor="numberOfUsers" className={styles.name_label}>
                No.of Users <span className={styles.required_name}>*</span>
              </label>
              <input
                id="numberOfUsers"
                type="text"
                value={organisationInfo?.no_of_users}
                onChange={(e) => setOrganisationInfo({ ...organisationInfo, no_of_users: e.target.value })}
                className={styles.input_user}
                disabled={activeStatus === false && organisationInfo?.is_active} // Disable based on the `activeStatus` state
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="numberOfQuestions" className={styles.name_label}>
                No.of Questions <span className={styles.required_name}>*</span>
              </label>
              <div className={styles.radio_group}>
                <label className={styles.radio_label}>
                  <input
                    type="radio"
                    name="questions"
                    checked={!organisationInfo?.is_infinite}
                    onChange={() => {
                      setOrganisationInfo({ ...organisationInfo, is_infinite: false, no_of_questions: '' });
                    }}
                    className={styles.radio_checkmark}
                    disabled={activeStatus === false && organisationInfo?.is_active}
                  />
                  <span>Limited Questions</span>
                </label>
                <label className={styles.radio_label}>
                  <input
                    type="radio"
                    name="questions"
                    checked={organisationInfo?.is_infinite} // Infinite Questions should be checked when is_infinite is true
                    onChange={() => {
                      setOrganisationInfo({ ...organisationInfo, is_infinite: true, no_of_questions: '' });
                    }}
                    className={styles.radio_checkmark}
                    disabled={activeStatus === false && organisationInfo?.is_active}
                  />
                  <span>Infinite Questions</span>
                </label>
              </div>
            </div>

            {!organisationInfo?.is_infinite && (
              <div className={styles.formGroup}>
                <label htmlFor="setLimit" className={styles.name_label}>
                  Set Limit <span className={styles.required_name}>*</span>
                </label>
                <input
                  id="setLimit"
                  type="text"
                  value={organisationInfo?.no_of_questions}
                  onChange={(e) =>
                    setOrganisationInfo({
                      ...organisationInfo,
                      no_of_questions: e.target.value,
                    })
                  }
                  className={styles.input_limit}
                  disabled={activeStatus === false && organisationInfo?.is_active}
                />
              </div>
            )}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="statusActive" className={styles.name_label}>
              Status <span className={styles.required_name}>*</span>
            </label>
            <div className={styles.radio_group}>
              <label className={styles.radio_label}>
                <input
                  type="radio"
                  id="statusActive"
                  name="status"
                  value={organisationInfo?.is_active ? 'active' : 'inactive'}
                  checked={status === 'active'}
                  onChange={() => handleStatusChange('active')}
                  className={styles.radio_checkmark}
                />
                <span>Active</span>
              </label>
              <label className={styles.radio_label}>
                <input
                  type="radio"
                  name="status"
                  value="inactive"
                  checked={status === 'inactive'}
                  onChange={() => handleStatusChange('inactive')}
                  className={styles.radio_checkmark}
                />
                <span>Inactive</span>
              </label>
            </div>
          </div>
          <div className={styles.buttonGroup}>
            <button type="button" onClick={handleBackClick} className={styles.cancelButton}>
              Cancel
            </button>
            <button type="button" onClick={handleAddOrganization} className={styles.addButton}>
              {orgId ? 'Update' : 'Add'} Organization
            </button>
          </div>
        </form>
      </div>
      {showModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            {/* Modal Header with Close Button */}
            <div className={styles.modalHeader}>
              <button
                onKeyDown={(e) => {
                  if (e.key === 'Enter') closeModal();
                }}
                className={styles.modalClose}
                onClick={closeModal}
              >
                <img src={closeIcon} alt="Close" />
              </button>
            </div>

            {/* Modal Body */}
            <div className={styles.modalBody}>
              <p>Are you sure you want to Deactivate {organisationInfo?.name}?</p>
            </div>

            <div className={styles.modalTwoButtons}>
              <button
                className={styles.cancelButton}
                onClick={() => {
                  setShowModal(false);
                  setTempStatus('active'); // Revert selection
                }}
              >
                Back
              </button>
              <div className={styles.modalButtons}>
                <button className={styles.confirmButton} onClick={confirmStatusChange}>
                  Yes, Deactivate
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AuthLayout>
  );
};

export default CreateOrganization;
